/**
 * Organization picker page component.
 * Shown after login when user belongs to multiple organizations.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { config } from '../config';
import LoadingSpinner from '../components/LoadingSpinner';

function isValidReturnTo(url: string): boolean {
  if (url.startsWith('/')) return true; // relative paths are always safe
  try {
    const parsed = new URL(url);
    const base = config.baseDomain;
    return parsed.hostname === base || parsed.hostname.endsWith(`.${base}`);
  } catch {
    return false;
  }
}

export default function OrgPicker() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: authLoading,
    organizations,
    currentOrganization,
    switchOrganization,
  } = useAuth();
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get return URL — validate to prevent open redirect attacks
  const params = new URLSearchParams(location.search);
  const rawReturnTo = params.get('return_to') || config.defaultReturnUrl;
  const returnTo = isValidReturnTo(rawReturnTo) ? rawReturnTo : config.defaultReturnUrl;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleSelectOrg = async (orgId: string, setAsDefault: boolean) => {
    setError(null);

    // Switch to the selected org
    switchOrganization(orgId);

    if (setAsDefault) {
      setIsSettingDefault(true);
      try {
        // Set as default via gateway API
        const response = await fetch(`${config.gatewayUrl}/v1/admin/users/me/default-organization`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ organization_id: orgId }),
        });

        if (!response.ok) {
          throw new Error('Failed to set default organization');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set default');
        setIsSettingDefault(false);
        return;
      }
      setIsSettingDefault(false);
    }

    // Navigate to return URL
    window.location.href = returnTo;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (organizations.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-2">No Organizations</h1>
          <p className="text-content-secondary mb-6">
            You don't belong to any organizations yet. Contact your administrator to get invited.
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="btn-secondary"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-lg w-full">
        <div className="text-center mb-8">
          <img
            src="/orbital-passport-lm.svg"
            alt="OrbitalPassport"
            className="h-16 w-16 mx-auto mb-4 dark:hidden"
          />
          <img
            src="/orbital-passport-transparent.svg"
            alt="OrbitalPassport"
            className="h-16 w-16 mx-auto mb-4 hidden dark:block"
          />
          <h1 className="text-2xl font-bold text-content-primary">Choose Organization</h1>
          <p className="text-content-secondary mt-1">
            Select which organization you want to work with
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          {organizations.map((org) => (
            <div
              key={org.id}
              className={`p-4 border rounded-lg transition-all cursor-pointer
                         hover:border-orbital-400 hover:shadow-md
                         ${org.id === currentOrganization?.id ? 'border-orbital-500 bg-orbital-50' : 'border-border'}`}
              onClick={() => handleSelectOrg(org.id, false)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-orbital-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-medium">
                      {org.name?.charAt(0).toUpperCase() || 'O'}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-medium text-content-primary">{org.name}</h3>
                    <p className="text-sm text-content-tertiary capitalize">{org.role}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOrg(org.id, true);
                    }}
                    disabled={isSettingDefault}
                    className="text-xs text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 px-2 py-1 rounded
                               hover:bg-orbital-100 dark:hover:bg-orbital-900 transition-colors"
                    title="Set as default and continue"
                  >
                    {isSettingDefault ? 'Setting...' : 'Set Default'}
                  </button>
                  <svg className="w-5 h-5 text-content-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-content-tertiary mt-6">
          You can switch organizations anytime from the header menu
        </p>
      </div>
    </div>
  );
}
